import { TRANSCRIPT_REVIEW_PROPOSAL_SOURCE, TRANSCRIPT_REVIEW_PROPOSAL_STATUS } from '../constants';
import { TTranscriptChunkRange, TTranscriptReviewProposal, TTranscriptReviewSession } from '../types';
import { getChangedLineNumbers, getEventChunkIndex, getStringArray } from './events';
import { applyProposalPatch } from './transcript';

export const offsetPatch = (
  patch: TTranscriptReviewProposal['patch'],
  lineOffset: number,
): TTranscriptReviewProposal['patch'] => ({
  ...patch,
  changes: (patch.changes ?? []).map((change) => ({
    ...change,
    line: change.line + lineOffset,
  })),
});

export const offsetLineNumbers = (lineNumbers: number[], lineOffset: number): number[] =>
  lineNumbers.map((lineNumber) => lineNumber + lineOffset);

export const getChunkLineOffset = (chunks: TTranscriptChunkRange[], event: Record<string, unknown>): number => {
  const chunkIndex = getEventChunkIndex(event);
  if (chunkIndex === null) {
    return 0;
  }

  return (chunks.find((chunk) => chunk.index === chunkIndex)?.startLine ?? 1) - 1;
};

export const offsetStyleFixEventForTranscript = (
  event: Record<string, unknown>,
  chunks: TTranscriptChunkRange[],
): Record<string, unknown> => {
  const lineOffset = getChunkLineOffset(chunks, event);
  const patch = event.patch;
  const nextEvent = {
    ...event,
    fixtureChunkLineOffset: lineOffset,
    sequence:
      Number.isInteger(event.fixtureChunkIndex) && Number.isInteger(event.sequence)
        ? (event.fixtureChunkIndex as number) * 1000 + (event.sequence as number)
        : event.sequence,
  };

  if (!patch || typeof patch !== 'object' || !Array.isArray((patch as TTranscriptReviewProposal['patch']).changes)) {
    return nextEvent;
  }

  return {
    ...nextEvent,
    changedLines: offsetLineNumbers(
      getChangedLineNumbers(event.changedLines).length
        ? getChangedLineNumbers(event.changedLines)
        : (patch as TTranscriptReviewProposal['patch']).changes?.map((change) => change.line) ?? [],
      lineOffset,
    ),
    patch: offsetPatch(patch as TTranscriptReviewProposal['patch'], lineOffset),
  };
};

export const buildLocalFixtureProposals = (
  session: TTranscriptReviewSession,
  events: Record<string, unknown>[],
  chunks: TTranscriptChunkRange[] = [],
): TTranscriptReviewProposal[] =>
  events.flatMap((event, index) => {
    const patch = event.patch;

    if (!patch || typeof patch !== 'object' || !Array.isArray((patch as TTranscriptReviewProposal['patch']).changes)) {
      return [];
    }

    const lineOffset = getChunkLineOffset(chunks, event);
    const chunkIndex = getEventChunkIndex(event);
    const sequence = Number(event.sequence ?? session.proposals.length + index + 1);

    return [
      {
        id: `fixture-proposal-${chunkIndex ?? 'all'}-${sequence}-${Date.now()}-${index}`,
        sessionId: session.id,
        sequence: chunkIndex === null ? sequence : chunkIndex * 1000 + sequence,
        status: TRANSCRIPT_REVIEW_PROPOSAL_STATUS.PENDING,
        source: TRANSCRIPT_REVIEW_PROPOSAL_SOURCE.FIXTURE,
        agentName: `${chunkIndex === null ? '' : `chunk ${chunkIndex} · `}${String(
          event.stepName ?? event.metricsAgentName ?? 'fixture',
        )}`,
        prompt: null,
        targetRevision: session.currentRevision,
        patch: offsetPatch(patch as TTranscriptReviewProposal['patch'], lineOffset),
        changedLines: offsetLineNumbers(
          getChangedLineNumbers(event.changedLines).length
            ? getChangedLineNumbers(event.changedLines)
            : (patch as TTranscriptReviewProposal['patch']).changes?.map((change) => change.line) ?? [],
          lineOffset,
        ),
        applyErrors: getStringArray(event.applyErrors),
        metadata: {
          applied: event.applied ?? null,
          chunkIndex,
          chunkLineOffset: lineOffset,
          timestamp: event.timestamp ?? null,
        },
        createdAt: new Date().toISOString(),
      },
    ];
  });

export const appendLocalPreviewProposals = (
  session: TTranscriptReviewSession,
  proposals: TTranscriptReviewProposal[],
): TTranscriptReviewSession => ({
  ...session,
  proposals: [
    ...session.proposals,
    ...proposals.map((proposal, index) => ({
      ...proposal,
      sessionId: session.id,
      sequence: session.proposals.length + index + 1,
      status: proposal.status || TRANSCRIPT_REVIEW_PROPOSAL_STATUS.PENDING,
      targetRevision: session.currentRevision,
    })),
  ].sort((left, right) => left.sequence - right.sequence),
  updatedAt: new Date().toISOString(),
});

export const acceptLocalFixtureProposal = (
  session: TTranscriptReviewSession,
  proposalId: string,
): TTranscriptReviewSession => {
  const proposal = session.proposals.find((item) => item.id === proposalId);
  if (!proposal || proposal.status !== TRANSCRIPT_REVIEW_PROPOSAL_STATUS.PENDING) {
    return session;
  }

  const result = applyProposalPatch(session.draftTranscript, proposal);
  const accepted = result.errors.length === 0;

  return {
    ...session,
    draftTranscript: accepted ? result.transcript : session.draftTranscript,
    currentRevision: accepted ? session.currentRevision + 1 : session.currentRevision,
    proposals: session.proposals.map((item) =>
      item.id === proposalId
        ? {
            ...item,
            status: accepted ? TRANSCRIPT_REVIEW_PROPOSAL_STATUS.ACCEPTED : TRANSCRIPT_REVIEW_PROPOSAL_STATUS.STALE,
            changedLines: result.changedLines,
            applyErrors: result.errors,
          }
        : item,
    ),
    updatedAt: new Date().toISOString(),
  };
};

export const rejectLocalFixtureProposal = (
  session: TTranscriptReviewSession,
  proposalId: string,
): TTranscriptReviewSession => ({
  ...session,
  proposals: session.proposals.map((item) =>
    item.id === proposalId ? { ...item, status: TRANSCRIPT_REVIEW_PROPOSAL_STATUS.REJECTED } : item,
  ),
  updatedAt: new Date().toISOString(),
});

export const appendLocalFixtureEvents = (
  targetSession: TTranscriptReviewSession,
  events: Record<string, unknown>[],
  chunks: TTranscriptChunkRange[],
): TTranscriptReviewSession => ({
  ...targetSession,
  proposals: [...targetSession.proposals, ...buildLocalFixtureProposals(targetSession, events, chunks)].sort(
    (left, right) => left.sequence - right.sequence,
  ),
  updatedAt: new Date().toISOString(),
});

export const getNextPendingProposalId = (
  proposals: TTranscriptReviewProposal[],
  currentProposalId: string,
): string | null => {
  const currentIndex = proposals.findIndex((proposal) => proposal.id === currentProposalId);
  const orderedProposals =
    currentIndex === -1 ? proposals : [...proposals.slice(currentIndex + 1), ...proposals.slice(0, currentIndex)];

  return orderedProposals.find((proposal) => proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.PENDING)?.id ?? null;
};

export const getProposalChunkIndex = (proposal: TTranscriptReviewProposal): number | null => {
  const chunkIndex = proposal.metadata?.chunkIndex;

  return Number.isInteger(chunkIndex) ? (chunkIndex as number) : null;
};

export const getLoadedS3ChunkIndexesFromSession = (targetSession: TTranscriptReviewSession): Set<number> =>
  new Set(
    targetSession.proposals.flatMap((proposal) => {
      const chunkIndex = getProposalChunkIndex(proposal);
      return chunkIndex === null ? [] : [chunkIndex];
    }),
  );

export const getFirstProposalLine = (proposal: TTranscriptReviewProposal): number | null => {
  const line = proposal.patch.changes?.[0]?.line;

  return typeof line === 'number' && Number.isInteger(line) ? line : null;
};

export const getProposalMetadataNumber = (proposal: TTranscriptReviewProposal, key: string): number | null => {
  const value = proposal.metadata?.[key];
  return Number.isFinite(value) ? (value as number) : null;
};

export const getProposalLlmTokenCount = (proposal: TTranscriptReviewProposal): number | null => {
  const llmMetrics = proposal.metadata?.llmMetrics;
  if (!llmMetrics || typeof llmMetrics !== 'object' || Array.isArray(llmMetrics)) {
    return null;
  }

  const totalTokens = (llmMetrics as Record<string, unknown>).totalTokens;
  return Number.isFinite(totalTokens) ? (totalTokens as number) : null;
};

export const getProposalScopeLabel = (proposal: TTranscriptReviewProposal): string => {
  const mode = typeof proposal.metadata?.mode === 'string' ? proposal.metadata.mode : '';
  const startLine = getProposalMetadataNumber(proposal, 'startLine');
  const endLine = getProposalMetadataNumber(proposal, 'endLine');

  if (mode && startLine && endLine) {
    return `${mode} L${startLine}-${endLine}`;
  }

  if (mode) {
    return mode;
  }

  return '';
};
